import { Action, action, thunk, Thunk } from 'easy-peasy'
import {
    addMoveToGTPosition,
    createRootGameTree,
    getMoveById,
    getNextPosition,
    getPreviousPosition,
    getStartingPosition,
    removeVariationsFromGameTree,
} from '../../chess/gameTree'
import { gameFromPGN, pgnParseHeaders } from '../../chess/pgn'
import { BasePositionFEN } from '../../chess/positionPresets'
import { Color, GameTree, Move } from '../../chess/types'
import { Arrow, Square } from '../../react-chessboard/src/chessboard/types'
import { StoreModel } from '../../store/store'
import { GameData, GameResultType, PGNHeader } from '../../store/types'
import { getGameHistoryByID } from '../Profile/API'
import { getGameResultType } from '../gameView/components/gameOverDialog/utils'
import { GAME_END_REASONS } from '../gameView/components/gameServerConnector/GSTypes'
import { BasePGNHeaders } from './utils'

type ArrowData = {
    from: Square
    to: Square
    score: number
    scoreType: string
    key: string
}

export type GameAnalysisModel = {
    flipped: boolean
    deleteModeOn: boolean
    gameTree: GameTree
    currentPositionId: string
    myColor: Color
    colorToMove: number
    fen: string
    playComputerFen: string
    resultType?: GameResultType
    customArrows: Array<Arrow>
    arrowsData: Array<ArrowData>
    setGameTree: Action<GameAnalysisModel, GameTree>
    setCurrentPositionId: Action<GameAnalysisModel, string>
    setFlipBoard: Action<GameAnalysisModel, boolean>
    setDeleteModeOn: Action<GameAnalysisModel, boolean>
    initialiseGame: Action<GameAnalysisModel, { gameData: GameData; userID: string }>
    addVariationMove: Action<GameAnalysisModel, Move>
    setGameTreeWithFen: Action<GameAnalysisModel, string>
    setMoveAnnotation: Action<GameAnalysisModel, { moveID: string; annotation: string }>
    setColorToMove: Action<GameAnalysisModel, number>
    setFen: Action<GameAnalysisModel, string>
    setPlayComputerFen: Action<GameAnalysisModel, string>
    setToPreviousPosition: Action<GameAnalysisModel>
    setToNextPosition: Action<GameAnalysisModel>
    setResultType: Action<GameAnalysisModel, GameResultType | undefined>
    setGameForAnalysisById: Thunk<GameAnalysisModel, string, {}, StoreModel>
    setCustomArrow: Action<GameAnalysisModel, ArrowData>
    clearCustomArrows: Action<GameAnalysisModel>
    clearArrowData: Action<GameAnalysisModel>
}

const initialGameTree = createRootGameTree(BasePositionFEN)

const AnalysisViewModel: GameAnalysisModel = {
    myColor: Color.White,
    flipped: false,
    deleteModeOn: false,
    gameTree: initialGameTree,
    currentPositionId: getStartingPosition(initialGameTree).id,
    colorToMove: 1,
    fen: '',
    playComputerFen: BasePositionFEN,
    resultType: undefined,
    customArrows: [],
    arrowsData: [],

    // ----------------------------------------------------------------------------- actions
    setGameTree: action((state, payload) => {
        state.gameTree = payload
        state.currentPositionId = getStartingPosition(payload).id
    }),

    setCurrentPositionId: action((state, payload) => {
        state.currentPositionId = payload
    }),

    setFlipBoard: action((state, flipped) => {
        state.flipped = flipped
    }),

    setFen: action((state, fen) => {
        state.fen = fen
    }),

    setDeleteModeOn: action((state, deleteModeOn) => {
        state.deleteModeOn = deleteModeOn
    }),

    setPlayComputerFen: action((state, fen) => {
        state.playComputerFen = fen
    }),

    addVariationMove: action((state, newMove) => {
        if (state.gameTree && state.currentPositionId) {
            let position = state.gameTree.positions.find((p) => p.id === state.currentPositionId)
            if (!!position) {
                const GameTreePosition = addMoveToGTPosition(state.gameTree, position, newMove)
                state.currentPositionId = GameTreePosition.id
                // TODO: check if it's don't affect the analysis and remove it
                // state.myColor = GameTreePosition.position.turn === Color.White ? Color.Black : Color.White
            }
        }
    }),

    initialiseGame: action((state, gd) => {
        const newRoot = createRootGameTree(gd.gameData.startingFen || BasePositionFEN)
        state.gameTree = newRoot
        state.currentPositionId = getStartingPosition(newRoot).id
        let myColor = Color.White
        if (gd.gameData.player2.playerId === gd.userID) {
            myColor = Color.Black
            state.flipped = true
        }
        state.myColor = myColor
    }),

    setGameTreeWithFen: action((state, fen) => {
        const newRoot = createRootGameTree(fen)
        state.gameTree = newRoot
        state.currentPositionId = getStartingPosition(newRoot).id
    }),

    setMoveAnnotation: action((state, { moveID, annotation }) => {
        if (!state.gameTree) return
        const m = getMoveById(state.gameTree, moveID)
        if (m) m.annotation = annotation
    }),
    setColorToMove: action((state, number) => {
        state.colorToMove = number
    }),

    setToPreviousPosition: action((state) => {
        if (!state.gameTree || !state.currentPositionId) return
        const prevPosition = getPreviousPosition(state.gameTree, state.currentPositionId)
        if (prevPosition) state.currentPositionId = prevPosition.id
    }),

    setToNextPosition: action((state) => {
        if (!state.gameTree || !state.currentPositionId) return
        const nextPosition = getNextPosition(state.gameTree, state.currentPositionId)
        if (nextPosition) state.currentPositionId = nextPosition.id
    }),

    setResultType: action((state, resultType) => {
        state.resultType = resultType
    }),

    setGameForAnalysisById: thunk(async (actions, matchId, helpers) => {
        const { getStoreActions } = helpers
        const storeActions = getStoreActions()
        const matchData = await getGameHistoryByID(matchId)
        let pgnHeader: PGNHeader = BasePGNHeaders
        let gameTree: GameTree
        let resultType: GameResultType | undefined
        if (matchData && !matchData.error) {
            resultType = getGameResultType(matchData.reason as GAME_END_REASONS)
            const headers = pgnParseHeaders(matchData.pgn)
            pgnHeader = headers[0] as PGNHeader
            gameTree = gameFromPGN(matchData.pgn)
        } else {
            const newGame = createRootGameTree(BasePositionFEN)
            gameTree = removeVariationsFromGameTree(newGame)
        }
        actions.setResultType(resultType)
        storeActions.setPGNHeader(pgnHeader)
        actions.setGameTree(gameTree)
        storeActions.setActiveAnalysisTab(0)
    }),

    setCustomArrow: action((state, arrow) => {
        const foundIndex = state.arrowsData.findIndex((a) => a.key === arrow.key)
        let updatedArray: Array<ArrowData> = [...state.arrowsData]

        if (foundIndex !== -1) {
            updatedArray[foundIndex] = { ...updatedArray[foundIndex], ...arrow }
        } else {
            updatedArray.push(arrow)
        }

        state.arrowsData = updatedArray
        state.customArrows = state.arrowsData.map((arrowData, index) => [
            arrowData.from,
            arrowData.to,
            '#E0BF14',
            index === 0 ? 0.8 : index === 1 ? 0.4 : 0.2,
        ])
    }),

    clearCustomArrows: action((state) => {
        state.arrowsData = []
        state.customArrows = []
    }),
    clearArrowData: action((state) => {
        state.arrowsData = []
    }),
}

export default AnalysisViewModel
